import React from 'react';
import { LoadingSpinner } from 'components/common';
import PropTypes from 'prop-types';
import {
  FiBriefcase, FiClock, FiCalendar, FiXCircle, FiTrendingUp, FiCheckCircle,
} from 'react-icons/fi';
import StatTile from '../shared/StatTile';

const fmt = (n) => Number(n || 0).toLocaleString('en-US');
const fmtW = (n) => `${Number(n || 0).toLocaleString('en-US', { maximumFractionDigits: 1 })} MT`;

// Year-over-year delta chip (vs same period last year). Neutral when no baseline.
const Yoy = ({ pct }) => {
  if (pct === null || pct === undefined) return null;
  const up = pct >= 0;
  return (
    <span className={`tw-font-semibold ${up ? 'tw-text-emerald-600' : 'tw-text-red-500'}`}>
      {up ? '▲' : '▼'} {Math.abs(pct)}%
    </span>
  );
};
Yoy.propTypes = { pct: PropTypes.number };

// Thin wrapper that keeps the drill-down click behavior while the tile
// visuals live in the shared StatTile.
const ClickableTile = ({ onClick, children }) => {
  if (!onClick) return <div className="tw-h-full">{children}</div>;
  return (
    <div
      onClick={onClick}
      className="tw-h-full tw-rounded tw-cursor-pointer tw-transition-all hover:tw-shadow-md hover:-tw-translate-y-0.5"
    >
      {children}
    </div>
  );
};
ClickableTile.propTypes = { onClick: PropTypes.func, children: PropTypes.node };

// Each KPI drills down to its matching record subset via
// onDrillDown(title, extraParams) — params mirror the backend filter set.
const JobEntrySummaryCards = ({ data, loading, onDrillDown }) => {
  if (loading && !data) {
    return (
      <div className="tw-bg-white tw-rounded tw-border tw-border-gray-100 tw-flex tw-justify-center tw-py-10">
        <LoadingSpinner size="md" />
      </div>
    );
  }
  if (!data) return null;

  const drill = (title, extraParams) =>
    onDrillDown ? () => onDrillDown(title, extraParams) : undefined;

  const wowDelta = (data.this_week || 0) - (data.last_week || 0);

  return (
    <div className="tw-grid tw-grid-cols-2 md:tw-grid-cols-3 xl:tw-grid-cols-6 tw-gap-3">
      <ClickableTile onClick={drill('All Job Entries', {})}>
        <StatTile label="Total Job Entries" value={fmt(data.total)}
          sub={(
            <>
              <span>{fmtW(data.total_weight)} {data.weight_yoy != null && <Yoy pct={data.weight_yoy} />}</span>
              <span className="tw-block tw-mt-0.5">
                {data.total_yoy != null && <><Yoy pct={data.total_yoy} />{' '}</>}
                vs {fmt(data.prev_total)} last yr
              </span>
            </>
          )}
          icon={FiCheckCircle} theme="indigo" />
      </ClickableTile>
      <ClickableTile onClick={drill('Prospective projects (P)', { category: 'P' })}>
        <StatTile label="Prospective (P)" value={fmt(data.prospective)}
          sub="Prospective projects"
          icon={FiTrendingUp} theme="cyan" />
      </ClickableTile>
      <ClickableTile onClick={drill('Signed Contract (J)', { category: 'J' })}>
        <StatTile label="Signed Contract (J)" value={fmt(data.became_job)}
          sub={`${data.became_job_rate || 0}% of entries`}
          icon={FiBriefcase} theme="green" />
      </ClickableTile>
      <ClickableTile onClick={drill('Lost jobs (L)', { category: 'L' })}>
        <StatTile label="Lost Jobs (L)" value={fmt(data.lost)}
          sub="Cancelled / Lost / Wait"
          icon={FiXCircle} theme="rose" />
      </ClickableTile>
      <StatTile label="This Week" value={fmt(data.this_week)}
        sub={`${wowDelta >= 0 ? '+' : ''}${fmt(wowDelta)} vs last week`}
        icon={FiCalendar} theme="violet" />
      <StatTile label="Last Week" value={fmt(data.last_week)}
        sub="Previous week"
        icon={FiClock} theme="amber" />
    </div>
  );
};

JobEntrySummaryCards.propTypes = {
  data: PropTypes.object,
  loading: PropTypes.bool,
  onDrillDown: PropTypes.func,
};
JobEntrySummaryCards.defaultProps = { data: null, loading: false, onDrillDown: null };

export default JobEntrySummaryCards;
